import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { buildCacheKey, offlineDb, type OutboxOperation } from '../offline/offline-db';
import { HttpHandlerService } from './http-handler.service';
import { HttpHeaders } from '@angular/common/http';
import { OfflineCacheService } from './offline-cache.service';
import { PostResult } from '../models/post-result';

@Injectable({
  providedIn: 'root'
})
export class OfflineSyncService {
  private http = inject(HttpHandlerService);
  private cache = inject(OfflineCacheService);
  private flushing = false;

  async flush(): Promise<void> {
    if (this.flushing) {
      return;
    }

    this.flushing = true;
    try {
      const pending = await offlineDb.outboxOperations
        .filter((op) => op.status === 'pending' || op.status === 'failed')
        .sortBy('createdAtUtc');

      for (const operation of pending) {
        await this.syncOne(operation);
      }
    } finally {
      this.flushing = false;
    }
  }

  private async syncOne(operation: OutboxOperation): Promise<void> {
    if (operation.retryCount >= 5) {
      return;
    }

    await offlineDb.outboxOperations.update(operation.outboxId, { status: 'syncing', lastError: null });

    try {
      await this.dispatch(operation);
      await offlineDb.outboxOperations.delete(operation.outboxId);
      await this.invalidateCaches(operation);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      await offlineDb.outboxOperations.update(operation.outboxId, {
        status: 'failed',
        retryCount: operation.retryCount + 1,
        lastError: message
      });
    }
  }

  private async dispatch(operation: OutboxOperation): Promise<void> {
    const payload = JSON.parse(operation.payloadJson);
    const headers = new HttpHeaders({ 'Idempotency-Key': operation.outboxId });

    switch (operation.operationType) {
      case 'JOB_SAVE': {
        const jobId = payload.jobId as number | undefined;
        let endpoint = 'Job/SaveJob';
        if (jobId && jobId > 0) {
          endpoint += `?jobID=${jobId}`;
        }
        await firstValueFrom(this.http.post<PostResult>(endpoint, payload.body, headers));
        break;
      }
      case 'JOB_FOLLOWUP':
        await firstValueFrom(
          this.http.post<PostResult>(`Job/LogFollowUpAttempt?jobId=${payload.jobId}`, payload.body, headers)
        );
        break;
      case 'JOB_RESCHEDULE':
        await firstValueFrom(
          this.http.post<PostResult>(`Job/RescheduleFollowUp?jobId=${payload.jobId}`, payload.body, headers)
        );
        break;
      case 'ENQUIRY_SAVE': {
        let url = 'Enquiry/Save';
        if (payload.enquiryId) {
          url += `?enquiryId=${payload.enquiryId}`;
        }
        await firstValueFrom(this.http.post<PostResult>(url, payload.body, headers));
        break;
      }
      case 'ENQUIRY_STATUS':
        await firstValueFrom(this.http.post('Enquiry/UpdateStatus', payload.body, headers));
        break;
      case 'NOTIFICATION_MARK_READ':
        await firstValueFrom(this.http.post('Notification/MarkRead', payload.body, headers));
        break;
      default:
        throw new Error(`Unsupported outbox operation: ${operation.operationType}`);
    }
  }

  private async invalidateCaches(operation: OutboxOperation): Promise<void> {
    if (operation.operationType.startsWith('JOB')) {
      await this.cache.invalidateNamespace('job_list');
      if (operation.entityId) {
        await offlineDb.cacheEntries.delete(buildCacheKey('job_detail', operation.clientId, String(operation.entityId)));
      }
    }

    if (operation.operationType.startsWith('ENQUIRY')) {
      await this.cache.invalidateNamespace('enquiry_list');
      if (operation.entityId) {
        await offlineDb.cacheEntries.delete(buildCacheKey('enquiry_detail', operation.clientId, String(operation.entityId)));
      }
    }
  }
}
